import React, { useEffect, useState } from "react";
import { AlertCircle, Check, ChevronDown, Clock, Loader2, Star, X } from "lucide-react";
import { toast } from "react-toastify";
import Navbar from "../../components/Navbar";
import CommerceEmptyState from "../../components/Commerce/CommerceEmptyState";
import ListPagination from "../../components/Commerce/ListPagination";
import { ReviewsService } from "../../requests/admin/ReviewsService";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const STATUS_STYLES = {
  pending: { pill: "bg-amber-50 text-amber-800 ring-amber-200", dot: "bg-amber-500", label: "Pending" },
  approved: { pill: "bg-emerald-50 text-emerald-800 ring-emerald-200", dot: "bg-emerald-500", label: "Approved" },
  rejected: { pill: "bg-rose-50 text-rose-700 ring-rose-200", dot: "bg-rose-500", label: "Rejected" },
};

const initialsOf = (name) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const dealImage = (deal) => {
  if (typeof deal?.image === "string" && deal.image) return deal.image;
  if (Array.isArray(deal?.images) && typeof deal.images[0] === "string") return deal.images[0];
  return "";
};

const reviewPhoto = (review) => (typeof review?.image === "string" ? review.image : "");

const StarScore = ({ value }) => {
  const rating = Math.min(5, Math.max(0, Number(value) || 0));
  return (
    <span className="inline-flex items-center gap-2" role="img" aria-label={`${rating} out of 5 stars`}>
      <span className="inline-flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((level) => (
          <Star
            key={level}
            className={`h-4 w-4 ${level <= rating ? "text-amber-400" : "text-gray-200"}`}
            fill="currentColor"
            strokeWidth={0}
            aria-hidden="true"
          />
        ))}
      </span>
      <span className="text-sm font-semibold tabular-nums text-gray-900">{rating.toFixed(1)}</span>
    </span>
  );
};

const ReviewDecisionSummary = ({ review }) => {
  const deal = review.deal || {};
  const title = deal.title || deal.product_name || "This deal";
  return (
    <div className="mt-4 rounded-xl bg-gray-50 px-3.5 py-3 ring-1 ring-inset ring-gray-100">
      <p className="text-sm font-semibold text-gray-900">{review.client_name || "Customer"}</p>
      <p className="mt-0.5 truncate text-sm text-gray-500">{title}</p>
      {review.comment ? (
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-gray-700">{review.comment}</p>
      ) : null}
    </div>
  );
};

const ReviewSkeleton = () => (
  <div className="animate-pulse overflow-hidden rounded-2xl border border-gray-100 bg-white">
    <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/70 px-5 py-4">
      <div className="h-12 w-12 rounded-xl bg-gray-200" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 w-48 rounded bg-gray-200" />
        <div className="h-3 w-32 rounded bg-gray-100" />
      </div>
    </div>
    <div className="space-y-3 px-5 py-5">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-gray-200" />
        <div className="h-3.5 w-36 rounded bg-gray-200" />
      </div>
      <div className="h-3 w-full max-w-xl rounded bg-gray-100" />
      <div className="h-3 w-2/3 max-w-md rounded bg-gray-100" />
    </div>
  </div>
);

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  useEffect(() => {
    setPage(1);
  }, [status, perPage]);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      setIsFetching(true);
      setError("");
      const result = await ReviewsService.getReviews({
        status: status || undefined,
        page,
        perPage,
      });
      if (ignore) return;
      if (result.success) {
        setReviews(result.reviews);
        setPagination(result.pagination);
      } else {
        setReviews([]);
        setPagination(null);
        setError(result.message);
      }
      setIsLoading(false);
      setIsFetching(false);
    };
    load();
    return () => {
      ignore = true;
    };
  }, [status, page, perPage, refreshKey]);

  const confirmApprove = async () => {
    if (!approving) return;
    setBusyId(approving.id);
    const result = await ReviewsService.approveReview(approving.id);
    setBusyId(null);
    if (!result.success) {
      toast.error(result.message);
      return;
    }
    toast.success("Review approved");
    setApproving(null);
    setRefreshKey((key) => key + 1);
  };

  const submitReject = async (event) => {
    event.preventDefault();
    const nextReason = reason.trim();
    if (!nextReason) {
      setReasonError("Add a reason the customer can see.");
      return;
    }
    setReasonError("");
    setBusyId(rejecting.id);
    const result = await ReviewsService.rejectReview(rejecting.id, nextReason);
    setBusyId(null);
    if (!result.success) {
      setReasonError(result.message);
      return;
    }
    toast.success("Review rejected");
    setRejecting(null);
    setReason("");
    setRefreshKey((key) => key + 1);
  };

  const hasFilters = Boolean(status);

  return (
    <div className="min-h-screen bg-gray-50/70">
      <Navbar page="Reviews" />
      <div className="px-4 py-6 mx-auto max-w-6xl sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reviews</h1>
            <p className="mt-1 text-sm text-gray-500">
              Approve a review to publish it, or reject it with a reason the customer can fix.
            </p>
          </div>
        </div>

        <div className="mt-5 flex gap-2 overflow-x-auto" role="tablist" aria-label="Review status">
          {STATUS_FILTERS.map((filter) => {
            const selected = status === filter.value;
            return (
              <button
                key={filter.label}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setStatus(filter.value)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  selected ? "bg-gray-900 text-white" : "bg-white text-gray-600 ring-1 ring-gray-200 hover:text-gray-900"
                }`}
              >
                {filter.label}
              </button>
            );
          })}
        </div>

        <div className={`mt-5 transition-opacity ${isFetching && !isLoading ? "opacity-60" : ""}`}>
          {isLoading ? (
            <div className="space-y-4" role="status" aria-label="Loading reviews">
              {[0, 1, 2].map((row) => (
                <ReviewSkeleton key={row} />
              ))}
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-gray-100 bg-white px-6 py-16 text-center shadow-sm">
              <p className="text-sm text-gray-600">{error}</p>
              <button
                type="button"
                onClick={() => setRefreshKey((key) => key + 1)}
                className="mt-4 rounded-xl bg-[#FF6C6C] px-4 py-2.5 text-sm font-medium text-white"
              >
                Try again
              </button>
            </div>
          ) : reviews.length === 0 ? (
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
              <CommerceEmptyState
                title={status ? "No reviews found" : "No reviews yet"}
                description={
                  status
                    ? "Try another status."
                    : "Customer reviews will show up here."
                }
                hasActiveFilters={hasFilters}
                onClearFilters={() => setStatus("")}
                onRefresh={() => setRefreshKey((key) => key + 1)}
              />
            </div>
          ) : (
            <ul className="space-y-4">
              {reviews.map((review) => {
                const deal = review.deal || {};
                const productTitle = deal.title || deal.product_name || "Deal";
                const productDetail = deal.product_name && deal.product_name !== productTitle ? deal.product_name : "";
                const productPhoto = dealImage(deal);
                const photo = reviewPhoto(review);
                const name = review.client_name || "Customer";
                const when = formatDate(review.created_at);
                const reviewStatus = String(review.status || "pending").toLowerCase();
                const pending = reviewStatus === "pending";
                const statusStyle = STATUS_STYLES[reviewStatus] || STATUS_STYLES.pending;
                const busy = busyId === review.id;
                const open = openId === review.id;
                const panelId = `review-panel-${review.id}`;
                return (
                  <li key={review.id}>
                    <article className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition-shadow hover:shadow-md ${open ? "border-gray-300" : "border-gray-200/80"}`}>
                      <h2>
                        <button
                          type="button"
                          aria-expanded={open}
                          aria-controls={panelId}
                          onClick={() => setOpenId(open ? null : review.id)}
                          className={`flex w-full items-start gap-3 px-4 py-4 text-left transition-colors hover:bg-gray-50 sm:items-center sm:gap-4 sm:px-5 ${open ? "border-b border-gray-100 bg-gray-50/70" : ""}`}
                        >
                        {productPhoto ? (
                          <img
                            src={productPhoto}
                            alt=""
                            className="h-12 w-12 shrink-0 rounded-xl bg-white object-cover ring-1 ring-gray-200 sm:h-14 sm:w-14"
                          />
                        ) : (
                          <div className="h-12 w-12 shrink-0 rounded-xl bg-gray-200 sm:h-14 sm:w-14" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-gray-900 sm:text-base">{productTitle}</p>
                          {productDetail ? (
                            <p className="mt-0.5 truncate text-xs text-gray-500 sm:text-sm">{productDetail}</p>
                          ) : null}
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                            {deal.company_name ? (
                              <span className="inline-flex items-center gap-1.5">
                                {deal.company_logo ? (
                                  <img src={deal.company_logo} alt="" className="h-4 w-4 rounded-full object-cover" />
                                ) : null}
                                {deal.company_name}
                              </span>
                            ) : null}
                            {review.order_code ? (
                              <span className="rounded-md bg-white px-1.5 py-0.5 font-mono text-[11px] text-gray-600 ring-1 ring-gray-200">
                                {review.order_code}
                              </span>
                            ) : null}
                            <span className="text-gray-700">{name}</span>
                          </div>
                        </div>
                        <span className="flex shrink-0 items-center gap-2">
                          <span className="hidden sm:inline-flex">
                            <StarScore value={review.rating} />
                          </span>
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${statusStyle.pill}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${statusStyle.dot}`} aria-hidden="true" />
                            {statusStyle.label}
                          </span>
                          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
                        </span>
                        </button>
                      </h2>

                      <div id={panelId} inert={open ? undefined : true} className={`grid transition-[grid-template-rows] duration-200 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                        <div className="overflow-hidden">
                      <div className="px-4 py-5 sm:px-5">
                        <div className="mb-4 sm:hidden">
                          <StarScore value={review.rating} />
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FF6C6C]/10 text-sm font-semibold text-[#e65e5e]" aria-hidden="true">
                              {initialsOf(name)}
                            </span>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-gray-900">{name}</p>
                              {when ? <p className="text-xs text-gray-500">{when}</p> : null}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start">
                          <div className="min-w-0 flex-1">
                            {review.comment ? (
                              <p className="max-w-prose whitespace-pre-line text-[15px] leading-relaxed text-gray-800">
                                {review.comment}
                              </p>
                            ) : (
                              <p className="text-sm italic text-gray-400">No written comment.</p>
                            )}
                          </div>
                          {photo ? (
                            <a
                              href={photo}
                              target="_blank"
                              rel="noreferrer"
                              className="group relative block h-28 w-28 shrink-0 overflow-hidden rounded-xl ring-1 ring-gray-200"
                              aria-label="Open the photo attached to this review"
                            >
                              <img
                                src={photo}
                                alt="Attached to this review"
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                            </a>
                          ) : null}
                        </div>

                        {reviewStatus === "rejected" && review.rejection_reason ? (
                          <div className="mt-4 flex gap-2.5 rounded-xl bg-rose-50 px-3.5 py-3 text-sm text-rose-900 ring-1 ring-inset ring-rose-100">
                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" aria-hidden="true" />
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-rose-700">Reason sent to customer</p>
                              <p className="mt-0.5 leading-relaxed">{review.rejection_reason}</p>
                            </div>
                          </div>
                        ) : null}
                      </div>

                      {pending ? (
                        <footer className="flex flex-col-reverse gap-3 border-t border-gray-100 bg-gray-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                          <p className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                            Waiting for your decision
                          </p>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setRejecting(review);
                                setReason("");
                                setReasonError("");
                              }}
                              disabled={busy}
                              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-white px-4 py-2 text-sm font-medium text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-50 disabled:opacity-60 sm:flex-none"
                            >
                              <X className="h-4 w-4" aria-hidden="true" />
                              Reject
                            </button>
                            <button
                              type="button"
                              onClick={() => setApproving(review)}
                              disabled={busy}
                              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-60 sm:flex-none"
                            >
                              {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
                              Approve
                            </button>
                          </div>
                        </footer>
                      ) : reviewStatus === "approved" && review.approved_at ? (
                        <footer className="border-t border-gray-100 bg-gray-50/70 px-4 py-3 text-xs text-gray-500 sm:px-5">
                          Published {formatDate(review.approved_at)}
                        </footer>
                      ) : null}
                        </div>
                      </div>
                    </article>
                  </li>
                );
              })}
            </ul>
          )}
          {pagination ? (
            <div className="mt-4 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm [&>div]:border-t-0">
              <ListPagination
                pagination={pagination}
                isFetching={isFetching}
                perPage={perPage}
                onPerPageChange={setPerPage}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </div>
      </div>

      {approving ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
          role="presentation"
          onClick={() => {
            if (busyId !== approving.id) setApproving(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="approve-review-title"
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="approve-review-title" className="text-lg font-semibold text-gray-900">
              Approve this review?
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              It will be published on the product page. This cannot be undone from here.
            </p>
            <ReviewDecisionSummary review={approving} />
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setApproving(null)}
                disabled={busyId === approving.id}
                className="rounded-xl px-3.5 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmApprove}
                disabled={busyId === approving.id}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                {busyId === approving.id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
                Confirm approval
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {rejecting ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
          role="presentation"
          onClick={() => {
            if (busyId !== rejecting.id) setRejecting(null);
          }}
        >
          <form
            onSubmit={submitReject}
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-review-title"
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="reject-review-title" className="text-lg font-semibold text-gray-900">
              Reject this review?
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              The customer sees this reason and can send a replacement while it stays rejected.
            </p>
            <ReviewDecisionSummary review={rejecting} />
            <label htmlFor="rejection-reason" className="mt-4 block text-sm font-medium text-gray-800">
              Reason
            </label>
            <textarea
              id="rejection-reason"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setReasonError("");
              }}
              rows={4}
              autoFocus
              className="mt-1.5 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-[#FF6C6C] focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20"
              placeholder="Comment includes a phone number."
            />
            {reasonError ? (
              <p className="mt-2 text-sm text-rose-700" role="alert">
                {reasonError}
              </p>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRejecting(null)}
                disabled={busyId === rejecting.id}
                className="rounded-xl px-3.5 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busyId === rejecting.id}
                className="inline-flex items-center gap-1.5 rounded-xl bg-rose-600 px-3.5 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {busyId === rejecting.id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                Confirm rejection
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default Reviews;
